import {
  Check,
  Comment,
  Copy as CopyIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Note as NoteIcon,
  Settings as SettingsIcon,
} from '@tunasong/icons'
import {
  EditorQueries,
  getActionsComponent,
  getActiveView,
  getContentViews,
  getPlugin,
  runEditorCommand,
  useEditor,
  useGlobalAppContext,
} from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import { isEntity, isPersisted } from '@tunasong/schemas'
import {Divider, Hotkey, makeLabel, shortcuts, useCmdHotkey, useNavigateToEntity, useUserConfig, MenuItem, MenuList, ListItemIcon, ListItemText, ListItemSecondaryAction, capitalize} from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import styles from './block-menu.module.css'
import { EditorPopup } from '../editor/editor-popup.js'
import { findElementPath } from '../hooks/use-element-path.js'

export interface BlockMenuProps {
  block: CoreElement
  open: boolean
  anchorEl: HTMLElement | null
  onShowProperties?(): void
  onComments?(): void
  /** Add a private personal note anchored to this block (M4 of anchored-notes). */
  onAddNote?(): void
  onClose?(): void
}

/** A Block. Extends Box, so all Box */
export const BlockMenu: FC<BlockMenuProps> = props => {
  /** @todo Factor this out into a useHotKey(cmd) */
  const blockDeleteCmd = useCmdHotkey('Block: Delete')
  const blockDuplicateCmd = useCmdHotkey('Block: Duplicate')
  const blockPropertiesCmd = useCmdHotkey('Block: Properties')

  const { block, onComments, onShowProperties, onAddNote, open, anchorEl, onClose } = props
  const navigateToEntity = useNavigateToEntity()
  const editor = useEditor()
  const plugins = editor.meta.pluginList
  const globalContext = useGlobalAppContext()
  const [userConfig] = useUserConfig()

  const handleClose = () => onClose && onClose()

  const openAndClose = (setProp: (prop: boolean) => void) => () => {
    setProp(true)
    handleClose()
  }

  /**
   * Resolve the path when the item is picked, not once at render: a block inserted above while the menu
   * is open shifts this one, and a captured path would delete/duplicate the neighbour (GH #1953/#1958).
   */
  const handleDeleteBlock = () => {
    const path = findElementPath(editor, block)
    if (!path) {
      handleClose()
      return
    }
    void runEditorCommand({
      commandId: 'block-delete',
      globalContext,
      editor,
      plugins,
      nodeEntry: [block, path],
      userConfig,
    })
    handleClose()
  }

  const handleDuplicateBlock = () => {
    const path = findElementPath(editor, block)
    if (!path) {
      handleClose()
      return
    }
    void runEditorCommand({
      commandId: 'block-duplicate',
      globalContext,
      editor,
      plugins,
      nodeEntry: [block, path],
      userConfig,
    })
    handleClose()
  }

  const handleOpen = (ev: MouseEvent) => {
    if (!(isPersisted(block) && isEntity(block))) {
      return
    }
    ev.preventDefault()

    handleClose()

    navigateToEntity(block)
  }

  const PluginMenuItems = getActionsComponent(plugins)

  /** @todo getting deep recursion issues here */
  const views = getContentViews(plugins as never, block.type as never)
  const currentView = getActiveView(block)

  const handleSetView = (view: string) => () => {
    handleClose()
    EditorQueries.updateElement(editor, block, { options: { ...block.options, activeView: view } })
  }

  // Get the name of the block, either from description
  const plugin = getPlugin(block, plugins)
  const blockName = capitalize(plugin?.description ?? block.type)

  return (
    <EditorPopup
      disablePortal={false}
      className={styles.popup}
      open={open}
      editor={editor}
      anchorEl={anchorEl}
      onClose={handleClose}
    >
      <MenuList className={styles.menuList} dense={true} autoFocusItem={false}>
        <MenuItem dense={true} disabled={true}>
          <ListItemText>{blockName}</ListItemText>
        </MenuItem>
        <Divider />

        {Object.keys(views).length > 0
          ? [
              <Divider key={1} />,
              <MenuItem key={2} title="Views" dense={true} disabled={true}>
                <ListItemText>Views</ListItemText>
              </MenuItem>,

              ...Object.keys(views).map(view => (
                <MenuItem key={view} title={view} disabled={currentView === view} onClick={handleSetView(view)}>
                  {currentView === view ? (
                    <ListItemIcon>
                      <Check />
                    </ListItemIcon>
                  ) : null}
                  <ListItemText inset={currentView !== view}>{capitalize(view)}</ListItemText>
                </MenuItem>
              )),
            ]
          : null}

        <PluginMenuItems element={block} onClose={handleClose} />

        {isPersisted(block) &&
          isEntity(block) && [
            <Divider key={0} />,
            <MenuItem key={1} autoFocus={true} dense={true} onClick={handleOpen}>
              <ListItemIcon>
                <LinkIcon />
              </ListItemIcon>
              <ListItemText>Open as Page</ListItemText>
            </MenuItem>,
          ]}
        <Divider />
        <MenuItem autoFocus={true} dense={true} onClick={handleDuplicateBlock}>
          <ListItemIcon>
            <CopyIcon />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
          {blockDuplicateCmd ? (
            <ListItemSecondaryAction>
              <Hotkey elevation={0}>{makeLabel()}</Hotkey>
            </ListItemSecondaryAction>
          ) : null}
        </MenuItem>

        <MenuItem dense={true} onClick={onShowProperties && openAndClose(onShowProperties)}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText>Properties</ListItemText>
          {blockPropertiesCmd ? (
            <ListItemSecondaryAction>
              <Hotkey elevation={0}>{makeLabel(blockPropertiesCmd.hotkey)}</Hotkey>
            </ListItemSecondaryAction>
          ) : null}
        </MenuItem>
        <MenuItem dense={true} onClick={onComments && openAndClose(onComments)}>
          <ListItemIcon>
            <Comment />
          </ListItemIcon>
          <ListItemText>Comments</ListItemText>
          <ListItemSecondaryAction>
            <Hotkey elevation={0}>{makeLabel(shortcuts.Comments.hotkey)}</Hotkey>
          </ListItemSecondaryAction>
        </MenuItem>
        {onAddNote ? (
          <MenuItem dense={true} onClick={openAndClose(onAddNote)}>
            <ListItemIcon>
              <NoteIcon />
            </ListItemIcon>
            <ListItemText>Add note here</ListItemText>
          </MenuItem>
        ) : null}
        <MenuItem dense={true} onClick={handleDeleteBlock}>
          <ListItemIcon>
            <DeleteIcon color="var(--tuna-palette-error-main)" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
          {blockDeleteCmd ? (
            <ListItemSecondaryAction>
              <Hotkey elevation={0}>{makeLabel(blockDeleteCmd.hotkey)}</Hotkey>
            </ListItemSecondaryAction>
          ) : null}
        </MenuItem>
      </MenuList>
      {/* <Typography sx={{ marginRight: 2, textAlign: 'right', display: 'block' }} variant="caption">
        {block.id}
      </Typography> */}
    </EditorPopup>
  )
}
